import { useEffect, useMemo, useState } from 'react'

import type { CompensationsPayrollRunNodeFragment } from 'generated/resources/graphql'

import { PayrollRunStatus } from 'modules/compensations/lib/constants'
import { isSourceStatus } from 'modules/compensations/lib/payrollRunStatus'

import type { SourceStatuses } from './PayrollRunStatusTransitionModal'

const STORAGE_KEY_PREFIX = 'factorial:compensationsCalculationToReviewModalShown'

export function usePayrollRunPreviousStatus(
  payrollRunId: number | undefined,
  status: string | undefined,
  payrollRun: CompensationsPayrollRunNodeFragment | undefined,
  isFeatureEnabled: boolean
) {
  const [statusBeforeInReviewTransition, setStatusBeforeInReviewTransition] = useState<
    SourceStatuses | undefined
  >()

  const storedPrevStatus = useMemo(() => {
    if (!payrollRunId) return undefined
    const key = `${STORAGE_KEY_PREFIX}_${payrollRunId}_prevStatus`
    const raw = localStorage.getItem(key)
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        return typeof parsed === 'string' ? parsed : undefined
      } catch {
        return undefined
      }
    }
    return undefined
  }, [payrollRunId])

  const setStoredPrevStatus = (value: string) => {
    if (payrollRunId) {
      const key = `${STORAGE_KEY_PREFIX}_${payrollRunId}_prevStatus`
      localStorage.setItem(key, JSON.stringify(value))
    }
  }

  useEffect(() => {
    if (!isFeatureEnabled || !payrollRun || !status) {
      return
    }

    // When status changes to IN_REVIEW and we don't have a stored previous status yet
    // this means the component mounted after the transition already happened
    if (status === PayrollRunStatus.IN_REVIEW && !storedPrevStatus) {
      // We can't recover the previous status, modal will use fallback
      return
    }

    // Handle real-time transition to IN_REVIEW
    if (
      status === PayrollRunStatus.IN_REVIEW &&
      storedPrevStatus &&
      isSourceStatus(storedPrevStatus)
    ) {
      setStatusBeforeInReviewTransition(storedPrevStatus)
      // Don't overwrite the stored status - we want to keep the original source status
      return
    }

    // Store the current status as the "previous status" for future transitions
    // But only if it's a valid source status (OPEN, IN_PREPARATION, CALCULATION)
    // Never store IN_REVIEW or COMPLETED as they are terminal/target states
    if (
      isSourceStatus(status) &&
      status !== PayrollRunStatus.IN_REVIEW &&
      status !== storedPrevStatus
    ) {
      setStoredPrevStatus(status)
    }
  }, [status, payrollRun, isFeatureEnabled, storedPrevStatus, setStoredPrevStatus])

  return {
    storedPrevStatus,
    statusBeforeInReviewTransition,
    setStatusBeforeInReviewTransition,
  }
}
